/**
 * Injects a JSON-LD script tag into <head>. Removes on unmount so route
 * changes don't leak structured data from a previous page.
 */
import { useEffect } from "react";

export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = `jsonld-${id}`;
    el.text = JSON.stringify(data);
    // Remove any prior instance with the same id
    const prior = document.getElementById(el.id);
    if (prior) prior.remove();
    document.head.appendChild(el);
    return () => {
      const found = document.getElementById(el.id);
      if (found) found.remove();
    };
  }, [id, JSON.stringify(data)]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function buildFaqSchema(faqs = []) {
  if (!faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** Return the deployed URL shape for an internal pathname. */
export function canonicalPath(value = "/") {
  const pathname = String(value || "/");
  if (pathname === "/" || pathname.endsWith("/") || /\/[^/]+\.[^/]+$/.test(pathname)) return pathname;
  return `${pathname}/`;
}

/** Build an absolute, trailing-slash canonical URL while preserving query/hash. */
export function absoluteUrl(path = "/") {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://mymoneylocal.com";
  const url = new URL(path, `${origin}/`);
  url.pathname = canonicalPath(url.pathname);
  return url.toString();
}

/**
 * Organization schema — helps Google's Knowledge Graph and AI search engines
 * (ChatGPT/Gemini/Perplexity/Bing Copilot) attribute content to the brand.
 */
export function buildOrganizationSchema({ url = "https://mymoneylocal.com/" } = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "MyMoneyLocal",
    url: absoluteUrl(url),
    logo: `${url.replace(/\/+$/, "")}/logo512.png`,
    sameAs: [],
    description:
      "MyMoneyLocal offers free personal finance calculators and plain-English guides for borrowing, investing, business, real estate, and vehicles.",
  };
}

/**
 * WebSite schema with SiteNavigationElement + SearchAction — exposes the
 * site's internal search to Google's sitelinks searchbox and AI crawlers.
 */
export function buildWebSiteSchema({ url = "https://mymoneylocal.com/" } = {}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "MyMoneyLocal",
    url: absoluteUrl(url),
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${absoluteUrl("/articles/")}?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildCalculatorSchema({ name, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name,
    description,
    url: absoluteUrl(url),
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    creator: { "@type": "Organization", name: "MyMoneyLocal" },
  };
}

/**
 * BreadcrumbList schema for rich-result breadcrumbs in Google.
 * items = [{ name, url }] — url must be absolute.
 */
export function buildBreadcrumbSchema(items = []) {
  if (!items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: absoluteUrl(it.url),
    })),
  };
}

